import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ProfileSettings from './ProfileSettings';
import Icon from './Icon';
import Toast from './Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/recipes';

interface LastPurge {
  id: number;
  item_count: number;
  created_at: string;
}

interface LastMealPlanPurge {
  id: number;
  meal_count: number;
  created_at: string;
}

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [generating, setGenerating] = useState(false);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [showLoadConfirm, setShowLoadConfirm] = useState(false);
  const [showMealPlanConfirm, setShowMealPlanConfirm] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const errorDetail = (error: unknown): string | null =>
    axios.isAxiosError(error) ? error.response?.data?.error ?? null : null;

  const { data: pantryItems } = useQuery({
    queryKey: ['inventory'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/inventory/`);
      return response.data as { id: number }[];
    },
  });

  const { data: lastPurge } = useQuery({
    queryKey: ['last-purge'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/inventory/purge/last/`);
      return response.data as LastPurge | null;
    },
  });

  const { data: meals } = useQuery({
    queryKey: ['meal-schedule'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/schedule/`);
      return response.data as { id: number }[];
    },
  });

  const { data: lastMealPlanPurge } = useQuery({
    queryKey: ['last-meal-plan-purge'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/schedule/purge/last/`);
      return response.data as LastMealPlanPurge | null;
    },
  });

  const purgeMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API_URL}/inventory/purge/`);
      return response.data as { purged_count: number };
    },
    onSuccess: ({ purged_count }) => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['last-purge'] });
      setShowPurgeConfirm(false);
      setMessage({
        type: 'success',
        text: purged_count > 0
          ? `Purged ${purged_count} item${purged_count === 1 ? '' : 's'} from your pantry. You can restore them below if this was a mistake.`
          : 'Pantry is already empty.',
      });
    },
    onError: (error) => {
      setShowPurgeConfirm(false);
      setMessage({ type: 'error', text: errorDetail(error) || 'Error purging pantry. Please try again.' });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API_URL}/inventory/purge/restore/`);
      return response.data as { restored_count: number };
    },
    onSuccess: ({ restored_count }) => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['last-purge'] });
      setMessage({ type: 'success', text: `Restored ${restored_count} item${restored_count === 1 ? '' : 's'} to your pantry.` });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error restoring pantry. Please try again.' });
    },
  });

  const formatPurgeDate = (iso: string) =>
    new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

  const purgeMealPlanMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API_URL}/schedule/purge/`);
      return response.data as { purged_count: number };
    },
    onSuccess: ({ purged_count }) => {
      queryClient.invalidateQueries({ queryKey: ['meal-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
      queryClient.invalidateQueries({ queryKey: ['last-meal-plan-purge'] });
      setShowMealPlanConfirm(false);
      setMessage({
        type: 'success',
        text: purged_count > 0
          ? `Deleted ${purged_count} meal${purged_count === 1 ? '' : 's'} from your plan. You can restore it below if this was a mistake.`
          : 'Meal plan is already empty.',
      });
    },
    onError: (error) => {
      setShowMealPlanConfirm(false);
      setMessage({ type: 'error', text: errorDetail(error) || 'Error deleting meal plan. Please try again.' });
    },
  });

  const restoreMealPlanMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API_URL}/schedule/purge/restore/`);
      return response.data as { restored_count: number };
    },
    onSuccess: ({ restored_count }) => {
      queryClient.invalidateQueries({ queryKey: ['meal-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
      queryClient.invalidateQueries({ queryKey: ['last-meal-plan-purge'] });
      setMessage({ type: 'success', text: `Restored ${restored_count} meal${restored_count === 1 ? '' : 's'} to your plan.` });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error restoring meal plan. Please try again.' });
    },
  });

  const loadSampleMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API_URL}/load-data/`);
      return response.data as { count: number };
    },
    onSuccess: ({ count }) => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['last-purge'] });
      setShowLoadConfirm(false);
      setMessage({
        type: 'success',
        text: `Loaded ${count} sample item${count === 1 ? '' : 's'}. If you had real pantry data, you can restore it above.`,
      });
    },
    onError: (error) => {
      setShowLoadConfirm(false);
      setMessage({ type: 'error', text: errorDetail(error) || 'Error loading data. Please try again.' });
    },
  });

  const handleGeneratePlan = async () => {
    try {
      setGenerating(true);
      setMessage(null);
      const response = await axios.post(`${API_URL}/generate-meal-plan/`, { days: 10 });
      const shortfall = response.data.shortfall || 0;
      setMessage({
        type: shortfall > 0 ? 'error' : 'success',
        text: shortfall > 0
          ? response.data.message
          : `${response.data.message} Redirecting to dashboard...`,
      });
      setTimeout(() => navigate('/'), shortfall > 0 ? 5000 : 2000);
    } catch (error) {
      const detail = axios.isAxiosError(error) ? error.response?.data?.error : null;
      setMessage({ type: 'error', text: detail || 'Error generating meal plan. Recipe generation can occasionally time out -- please try again.' });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="settings">
      <div className="settings-header">
        <h1><Icon name="gear" className="icon" />Settings</h1>
        <p>Configure your RecipeEngine system</p>
      </div>

      <div className="settings-grid">
        <div className="settings-card">
          <Icon name="clipboard" className="icon card-icon" />
          <h2>User Guide</h2>
          <p>New to RecipeEngine? Walk through the Pantry, Meal Plan, and every other feature.</p>
          <button onClick={() => navigate('/guide')} className="btn-secondary">
            <Icon name="book" className="icon icon-sm" />
            Open User Guide
          </button>
        </div>

        <ProfileSettings onMessage={setMessage} />

        <div className="settings-card">
          <Icon name="sparkle" className="icon card-icon" />
          <h2>Generate Meal Plan</h2>
          <p>Let AI discover recipes based on your pantry and create a 10-day meal schedule.</p>
          <button
            onClick={handleGeneratePlan}
            disabled={generating}
            className="btn-primary"
          >
            <Icon name={generating ? 'refresh' : 'sparkle'} className="icon icon-sm" />
            {generating ? 'Generating...' : 'Generate 10-Day Plan'}
          </button>
        </div>

        <div className="settings-card danger-zone">
          <Icon name="warning" className="icon card-icon" />
          <h2>Danger Zone</h2>

          <div className="danger-subsection">
            <h3>Pantry</h3>
            <p>Permanently remove every item from your pantry. Use this to start fresh.</p>

            {lastPurge && (
              <div className="purge-restore-banner">
                <p>
                  Pantry was purged on {formatPurgeDate(lastPurge.created_at)}
                  {' '}({lastPurge.item_count} item{lastPurge.item_count === 1 ? '' : 's'}).
                </p>
                <button
                  onClick={() => restoreMutation.mutate()}
                  disabled={restoreMutation.isPending}
                  className="btn-secondary"
                >
                  {restoreMutation.isPending ? 'Restoring...' : 'Restore Purged Items'}
                </button>
              </div>
            )}

            <button
              onClick={() => setShowPurgeConfirm(true)}
              disabled={purgeMutation.isPending || !pantryItems?.length}
              className="btn-danger"
            >
              <Icon name="trash" className="icon icon-sm" />
              Purge Pantry
            </button>
          </div>

          <div className="danger-subsection">
            <h3>Meal Plan</h3>
            <p>Permanently remove your entire meal plan and its shopping list.</p>

            {lastMealPlanPurge && (
              <div className="purge-restore-banner">
                <p>
                  Meal plan was deleted on {formatPurgeDate(lastMealPlanPurge.created_at)}
                  {' '}({lastMealPlanPurge.meal_count} meal{lastMealPlanPurge.meal_count === 1 ? '' : 's'}).
                </p>
                <button
                  onClick={() => restoreMealPlanMutation.mutate()}
                  disabled={restoreMealPlanMutation.isPending}
                  className="btn-secondary"
                >
                  {restoreMealPlanMutation.isPending ? 'Restoring...' : 'Restore Meal Plan'}
                </button>
              </div>
            )}

            <button
              onClick={() => setShowMealPlanConfirm(true)}
              disabled={purgeMealPlanMutation.isPending || !meals?.length}
              className="btn-danger"
            >
              <Icon name="trash" className="icon icon-sm" />
              Delete Meal Plan
            </button>
          </div>
        </div>

        <div className="settings-card">
          <Icon name="basket" className="icon card-icon" />
          <h2>Load Sample Data</h2>
          <p className="warning-note">
            Load sample pantry items with common ingredients to get started quickly.
            {' '}<Icon name="warning" className="icon icon-sm" /> This replaces everything currently in your pantry.
          </p>
          <button
            onClick={() => setShowLoadConfirm(true)}
            disabled={loadSampleMutation.isPending}
            className="btn-secondary"
          >
            {loadSampleMutation.isPending ? 'Loading...' : 'Load Sample Pantry'}
          </button>
        </div>

        <div className="settings-card">
          <Icon name="gear" className="icon card-icon" />
          <h2>System Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Version:</span>
              <span className="info-value">1.0.0 (Beta)</span>
            </div>
          </div>
        </div>
      </div>

      {message && (
        <Toast type={message.type} text={message.text} onDismiss={() => setMessage(null)} />
      )}

      {showPurgeConfirm && (
        <div className="modal-overlay" onClick={() => setShowPurgeConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowPurgeConfirm(false)}>✕</button>
            <h2><Icon name="warning" className="icon" />Purge Pantry?</h2>
            <p>
              This will permanently delete all {pantryItems?.length ?? 0} item{pantryItems?.length === 1 ? '' : 's'} currently
              in your pantry. You'll be able to restore them from this page afterward if this was a mistake.
            </p>
            <div className="form-actions">
              <button
                type="button"
                onClick={() => setShowPurgeConfirm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => purgeMutation.mutate()}
                disabled={purgeMutation.isPending}
                className="btn-danger"
              >
                {purgeMutation.isPending ? 'Purging...' : 'Yes, Purge Pantry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMealPlanConfirm && (
        <div className="modal-overlay" onClick={() => setShowMealPlanConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowMealPlanConfirm(false)}>✕</button>
            <h2><Icon name="warning" className="icon" />Delete Meal Plan?</h2>
            <p>
              This will permanently delete all {meals?.length ?? 0} meal{meals?.length === 1 ? '' : 's'} in your
              current plan, along with its shopping list. You'll be able to restore it from this page afterward
              if this was a mistake.
            </p>
            <div className="form-actions">
              <button
                type="button"
                onClick={() => setShowMealPlanConfirm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => purgeMealPlanMutation.mutate()}
                disabled={purgeMealPlanMutation.isPending}
                className="btn-danger"
              >
                {purgeMealPlanMutation.isPending ? 'Deleting...' : 'Yes, Delete Meal Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLoadConfirm && (
        <div className="modal-overlay" onClick={() => setShowLoadConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowLoadConfirm(false)}>✕</button>
            <h2><Icon name="warning" className="icon" />Load Sample Data?</h2>
            <p>
              This will delete all {pantryItems?.length ?? 0} item{pantryItems?.length === 1 ? '' : 's'} currently
              in your pantry and replace them with sample data. You'll be able to restore your current items
              from this page afterward if this was a mistake.
            </p>
            <div className="form-actions">
              <button
                type="button"
                onClick={() => setShowLoadConfirm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => loadSampleMutation.mutate()}
                disabled={loadSampleMutation.isPending}
                className="btn-danger"
              >
                {loadSampleMutation.isPending ? 'Loading...' : 'Yes, Load Sample Data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
